import React from "react";
import { Box, CircularProgress } from "@mui/material";
import WorkflowApprovalLive, { WorkflowApprovalDecided } from "./WorkflowApprovalLive";
import { WorkflowApprovalTarget } from "../../hooks/useWorkflowApprovalStage";
import WorkflowCancelLive from "./WorkflowCancelLive";
import WorkflowPauseLive from "./WorkflowPauseLive";
import WorkflowSilentStageNotice from "./WorkflowSilentStageNotice";
import WorkflowTaskLive from "./WorkflowTaskLive";
import WorkflowWebServiceWaitNotice from "./WorkflowWebServiceWaitNotice";
import { WorkflowLiveCase } from "./workflowLiveCase";
import { WorkflowInstanceTaskDto } from "../../types/workflowDto";
import { getWorkflowInstanceStatus } from "../../services/workflowService";
import { WorkflowRunDecline } from "../../hooks/useWorkflowRunInstance";
import { WorkflowCanvasType } from "./workflowBuilderData";
import WorkflowFormStage from "./WorkflowFormStage";
import WorkflowPifDetailsLive from "./WorkflowPifDetailsLive";
import WorkflowPifRouteStage from "./WorkflowPifRouteStage";
import WorkflowPifStartStage from "./WorkflowPifStartStage";
import { describeWorkflowPerson, WorkflowCanvas } from "./workflowBuilderData";
import { PifIdentityLookupOutcome, PifIdentityMatch } from "./workflowRunData";
import { PifApplicantSummary, PifDetailsValues } from "./pifDetailsModel";
import { PifStartAnswers } from "./pifCaseValues";
import { PIF_STAGE_KEYS } from "./pifWorkflowKeys";

/** Stage types the engine runs itself inside start / advance (Constants.cs StageTypes.Silent). */
const SILENT_STAGE_TYPES: WorkflowCanvasType[] = ["conditional", "webservice", "email", "finalize"];

export interface WorkflowRunStageBodyProps {
  stage: WorkflowCanvas;
  /** The stage's key for the bespoke PIF screens (stageKey, or its id when the server sent none). */
  stageKey: string;
  isPifScreen: boolean;
  /** The definition is the PIF - its Cancel stage explains a declined PIF. */
  isPif: boolean;
  reference: string;
  definitionName: string;
  identity: PifIdentityMatch | null;
  /** The PIF applicant line for the approval stages; null for every other definition. */
  applicant: PifApplicantSummary | null;
  fieldValues: Record<string, string>;
  /** Generic form stage: in-flight state, server field errors, and the save/submit banner. */
  formBusy: boolean;
  formFieldErrors: Record<string, string>;
  formBanner?: React.ReactNode;
  lookupIdentity: (value: string) => Promise<PifIdentityLookupOutcome>;
  /** A real case: pif-1's answers are being saved to it. */
  pifStartSaving?: boolean;
  /** A reopened case: the identity's name is still being looked up (see useWorkflowPifResumedIdentity). */
  pifIdentityResolving?: boolean;
  onPifStartContinue: (answers: PifStartAnswers) => void;
  onPifRouteContinue: () => void;
  /** pif-3: its answers by key and what to do with them. */
  pifDetails: {
    values: PifDetailsValues;
    onChange: (patch: PifDetailsValues) => void;
    onContinue: () => void;
    onSaveDraft?: () => void;
    busy: boolean;
    /** Send for approval is off — a new hire with no applicant record yet (re-enter the SSN). */
    continueBlocked?: boolean;
    serverErrors: Record<string, string>;
    banner?: React.ReactNode;
  };
  onFieldChange: (fieldId: string, value: string) => void;
  onSaveDraft: () => void;
  onFormContinue: () => void;
  /** The case's open stage — Task / Pause / Cancel act on the engine, and silent stages show that
   *  they run automatically. Omitted only for the PIF's own screens, which need no case behind them. */
  liveCase?: WorkflowLiveCase & {
    /** Reloads the case without the page loader - for background polling the user didn't start. */
    onRefreshQuietly: () => Promise<boolean>;
    /** This stage's materialized checklist tasks. */
    tasks: WorkflowInstanceTaskDto[];
    pausedUntil: string | null;
    onCancelled: (reason: string) => void;
    /** A Cancel stage: the caller may close the case (owner or admin). */
    canClose: boolean;
    ownerName: string | null;
    /** The latest rejected or cancelled approval, for "PIF not approved". */
    decline: WorkflowRunDecline | null;
  };
  /** The case's approval stage — approvals + decide against the engine. */
  liveApproval?: {
    target: WorkflowApprovalTarget;
    onDecided: (decided: WorkflowApprovalDecided) => Promise<boolean>;
    onReloadCase: () => Promise<boolean>;
  };
}

/** The current stage's body on the Test / Run screen, by stage type — or a bespoke PIF screen. */
const WorkflowRunStageBody: React.FC<WorkflowRunStageBodyProps> = (props) => {
  const { stage, stageKey, identity, applicant } = props;

  const renderPifScreen = () => {
    if (stageKey === PIF_STAGE_KEYS.start) {
      return (
        <>
          {props.formBanner}
          {props.pifStartSaving ? (
            <Box className="workflow-run__hint" role="status">
              <CircularProgress size={16} aria-hidden="true" /> Saving your answers…
            </Box>
          ) : (
            <WorkflowPifStartStage onContinue={props.onPifStartContinue} lookupIdentity={props.lookupIdentity} />
          )}
        </>
      );
    }
    if (props.pifIdentityResolving) {
      return (
        <Box className="workflow-run__hint" role="status">
          <CircularProgress size={16} aria-hidden="true" /> Loading the applicant…
        </Box>
      );
    }
    if (stageKey === PIF_STAGE_KEYS.route && identity && identity.flow !== "blocked") {
      return <WorkflowPifRouteStage flow={identity.flow} onContinue={props.onPifRouteContinue} />;
    }
    if (stageKey === PIF_STAGE_KEYS.details && identity) {
      return <WorkflowPifDetailsLive identity={identity} {...props.pifDetails} />;
    }
    return null;
  };

  // The flagship PIF ships bespoke screens for its first three stages (identity lookup, routing
  // by the lookup result, the PIF form), matched by stage KEY. Everything else — including PIF's
  // later stages — is rendered from the canvas configuration the Builder saved. On a real case
  // pif-1 and pif-3 save by field key (80432's definition), so there is no preview caveat.
  const pifScreen = props.isPifScreen ? renderPifScreen() : null;
  if (pifScreen) return pifScreen;

  const { liveCase } = props;
  if (liveCase) {
    // The web service runs in the background, so a case does wait on it (6.4); the other silent
    // stages run inside the engine's own call and a case should never sit on one.
    if (stage.type === "webservice") {
      // It polls the light status and re-reads the case only once it has moved (Pooja Pujara on
      // 80430, 2026-10-16) - quietly, so the full-page loader never flashes.
      return (
        <WorkflowWebServiceWaitNotice
          key={stage.id}
          stageId={liveCase.stageId}
          stageName={stage.name}
          rowVersionTag={liveCase.rowVersionTag}
          checkStatus={() => getWorkflowInstanceStatus(liveCase.instanceId)}
          onRefresh={liveCase.onRefreshQuietly}
        />
      );
    }
    if (SILENT_STAGE_TYPES.includes(stage.type)) {
      return <WorkflowSilentStageNotice onReload={() => void liveCase.onChanged()} />;
    }
    if (stage.type === "task") {
      return <WorkflowTaskLive key={stage.id} canvas={stage} liveCase={liveCase} tasks={liveCase.tasks} />;
    }
    if (stage.type === "pause") {
      return (
        <WorkflowPauseLive
          key={stage.id}
          config={stage.pause}
          owner={describeWorkflowPerson(stage.ownerSource, stage.owner)}
          pausedUntil={liveCase.pausedUntil}
          liveCase={liveCase}
        />
      );
    }
    if (stage.type === "cancel") {
      return (
        <WorkflowCancelLive
          key={stage.id}
          config={stage.cancel}
          liveCase={liveCase}
          stageKey={stageKey}
          isRealPif={props.isPif}
          canClose={liveCase.canClose}
          ownerName={liveCase.ownerName}
          decline={liveCase.decline}
          onCancelled={liveCase.onCancelled}
        />
      );
    }
  }

  if (stage.type === "form") {
    return (
      <WorkflowFormStage
        canvas={stage}
        values={props.fieldValues}
        onChange={props.onFieldChange}
        onSaveDraft={props.onSaveDraft}
        onContinue={props.onFormContinue}
        busy={props.formBusy}
        fieldErrors={props.formFieldErrors}
        banner={props.formBanner}
      />
    );
  }

  if (stage.type === "approval" && props.liveApproval) {
    return (
      <WorkflowApprovalLive
        key={`${props.liveApproval.target.instanceId}-${props.liveApproval.target.stageId}`}
        target={props.liveApproval.target}
        reference={props.reference}
        definitionName={props.definitionName}
        stageName={stage.name}
        applicant={applicant}
        onDecided={props.liveApproval.onDecided}
        onReloadCase={props.liveApproval.onReloadCase}
      />
    );
  }

  // Every stage the Run screen shows is a case's, so there is nothing to step through locally.
  return null;
};

export default WorkflowRunStageBody;
